import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useGetBingoEventQuery } from '@/api/queries/bingo';
import { AppRoute } from '@/common/app/routes';
import { useBingoCompletionListener } from '@/common/hooks/bingo-line-completion-listener';
import { Playdeck } from '@/common/lib/playdeck';
import { BingoActivityFeed } from '@/components/Bingo/BingoActivityFeed/BingoActivityFeed';
import { BingoBackground } from '@/components/Bingo/BingoBackground/BingoBackground';
import { BingoCompleteModal } from '@/components/Bingo/BingoCompleteModal/BingoCompleteModal';
import { BingoGrid } from '@/components/Bingo/BingoGrid/BingoGrid';
import { BingoInfoModal } from '@/components/Bingo/BingoInfoModal/BingoInfoModal';
import { BingoLineRewardsModal } from '@/components/Bingo/BingoLineRewardsModal/BingoLineRewardsModal';
import { BingoPotentialRewards } from '@/components/Bingo/BingoPotentialRewards/BingoPotentialRewards';
import { BingoRollButtons } from '@/components/Bingo/BingoRollButtons/BingoRollButtons';
import { ResourceTitle } from '@/components/ResourceTitle/ResourceTitle';
import { StringSmall } from '@/components/Typography/Typography';
import { initFromApi } from '@/redux/reducers/bingoReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

function formatTimeRemaining(endAt: string, serverNow: number): string {
  const end = new Date(endAt).getTime();
  const diff = Math.max(0, end - serverNow);
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  const h = hours % 24;

  if (days > 0) return `${days}d : ${h}h`;
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return `${h}h : ${minutes}m`;
}

const Bingo: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: event } = useGetBingoEventQuery();
  useBingoCompletionListener();
  const endAt = useAppSelector((state) => state.bingo.endAt);
  const serverNow = useAppSelector((state) => state.bingo.serverNow);
  const eventId = useAppSelector((state) => state.bingo.eventId);
  const cardIndex = useAppSelector((state) => state.bingo.cardIndex);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  useEffect(() => {
    if (event && !event.board) {
      navigate(AppRoute.MAIN, { replace: true });
    } else if (event?.board) {
      dispatch(initFromApi(event));
    }
  }, [event, dispatch, navigate]);

  useEffect(() => {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'bingo_page_opened',
      user_properties: {},
      event_properties: {},
    });
  }, []);

  const timeRemaining =
    endAt && serverNow ? formatTimeRemaining(endAt, serverNow) : '--';

  return (
    <main className={css.page}>
      <BingoBackground />

      <div className={css.content}>
        <ResourceTitle className={css.title}>Bingo #{cardIndex}</ResourceTitle>

        <StringSmall weight="medium" className={css.subtitle}>
          {t('Fill all tiles to get a reward')}
        </StringSmall>

        {/* Activity feed */}
        <BingoActivityFeed />

        {/* Timer & Leaderboard pills */}
        <div className={css.pillRow}>
          <button
            className={css.pill}
            onClick={() => setIsInfoOpen(true)}
            aria-label={t('About Bingo event')}
          >
            <div className={css.pillLeft}>
              <img src="/img/bingo/timer.png" alt="" className={css.pillIcon} />
              <span className={css.pillText}>{timeRemaining}</span>
            </div>
            <img
              src="/img/icons/arrow-go.svg"
              alt=""
              className={css.pillArrow}
            />
          </button>

          <button
            className={css.pill}
            onClick={() => navigate(AppRoute.BINGO_LEADERBOARD)}
          >
            <div className={css.pillLeft}>
              <img
                src="/img/leaderBoard/badge-small.svg"
                alt=""
                className={css.pillIcon}
              />
              <span className={css.pillText}>{t('Leaders')}</span>
            </div>
            <img
              src="/img/icons/arrow-go.svg"
              alt=""
              className={css.pillArrow}
            />
          </button>
        </div>

        {/* Progress bar */}
        {/* <BingoProgressBar /> */}

        {/* Super reward options */}
        <div className={css.rewardsRow}>
          <BingoPotentialRewards />
        </div>

        <div className={css.gameArea}>
          <BingoGrid />
          {/* <img
            src="/img/bingo/arrow-right.svg"
            alt=""
            className={css.gridArrowRight}
          /> */}
        </div>
      </div>

      {eventId && (
        <motion.div
          className={css.bottomActions}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4, ease: 'easeOut' }}
        >
          <BingoRollButtons />
        </motion.div>
      )}

      <BingoLineRewardsModal />
      <BingoCompleteModal />
      <BingoInfoModal
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
        endAtLabel={timeRemaining}
      />
    </main>
  );
};

export { Bingo };
